import { getCookie, setCookie } from "cookies-next";
import Cookies from "js-cookie";
import { GetServerSidePropsContext } from "next";
import { v4 as uuidv4 } from "uuid";

import { Winda } from "@brexhq/next/src/utils/Winda";

import { DATADOG_ID } from "./constants";

export const anonymousIdStorageKey = "brexAnonymousId";
export const userIdStorageKey = "brexUserId";
export const websiteAnonymousUsersKey = "website-anonymous-users";

export type AnonymousId = { readonly __tag: "AnonymousId" } & string;

const refreshCookie = (key: string, value?: string): void => {
  if (!value) return;
  Cookies.set(key, value, {
    expires: 365 * 10,
    domain: cookieRootDomain(window.location.host),
  });
};

const getWithRefresh = (key: string): string | undefined => {
  const value = Cookies.get(key);
  refreshCookie(key, value);
  return value;
};

// Generate a uuid for identifying anonymous users and store
// it in a cookie, refreshing the expiry date if it already exists.
// This allows us to make use of this ID before Segment is fully loaded,
// so we can initialize other libraries that need to use on the
// anonymous ID in parallel instead of blocking on segment's initialization.
// Also, for users with certain ad-blockers and tracking blocking extensions
// Segment's library may never actually load, so we can't always depend on
// this functionality from Segment's library.
export const getOrCreateAnonymousId = (anonymousIdStorageKey: string) => {
  if (!Winda.isPresent) return "" as AnonymousId;
  const existingAnonymousId = Cookies.get(anonymousIdStorageKey);

  const anonymousId = Winda.isDataDogBot
    ? DATADOG_ID
    : existingAnonymousId || uuidv4();

  // refresh the cookie regardless if it already exists
  Cookies.set(anonymousIdStorageKey, anonymousId, {
    expires: 365 * 10,
    domain: cookieRootDomain(window.location.host),
  });
  return anonymousId as AnonymousId;
};

export const getAnonymousId = (): AnonymousId => {
  return getOrCreateAnonymousId(anonymousIdStorageKey);
};

/**
 * We want to get/create anonymousId for server side (same concept as the getOrCreateAnonymousId function)
 */
export const getOrCreateAnonymousIdServerSide = (
  context: GetServerSidePropsContext,
) => {
  const { req, res } = context;
  const existingAnonymousId = getCookie(anonymousIdStorageKey, { req, res });
  const isDataDogBot = req?.headers?.["user-agent"]?.includes("Datadog");

  const anonymousId = isDataDogBot
    ? DATADOG_ID
    : existingAnonymousId || uuidv4();

  // set date to be ten years from now
  const date = new Date();
  date.setFullYear(date.getFullYear() + 10);

  setCookie(anonymousIdStorageKey, anonymousId, {
    req,
    res,
    expires: date,
    domain: cookieRootDomain(req?.headers?.host),
  });

  return anonymousId as AnonymousId;
};

export type UserId = { readonly __tag: "UserId" } & string;

export const getUserId = (): UserId => {
  if (Winda.isDataDogBot) return "DATADOG_SYNTHETICS_BROWSER" as UserId;
  return getWithRefresh(userIdStorageKey) as UserId;
};

export const setUserId = (id: string | undefined): void => {
  if (!id) {
    return Cookies.remove(userIdStorageKey);
  }

  Cookies.set(userIdStorageKey, id, {
    expires: 365 * 10,
    domain: cookieRootDomain(window.location.host),
  });
};

// We use a cookie for the anonymous ID so we can share it seamlessly between
// dashboard.brex.com and brex.com
// This requires actually computing the correct root domain to set for the cookie
export const cookieRootDomain = (
  url: string | undefined | null,
): string | undefined => {
  if (!url) return undefined;
  if (url.startsWith("localhost")) return "localhost";
  if (url.endsWith("staging.brex.com")) return ".staging.brex.com";
  if (url.endsWith("brex.com")) return ".brex.com";
  return undefined;
};
